import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";

// Dr. Earth cannabis dry-feeding schedule: 1/4 cup per 5-gallon container.
// 1/4 cup = 4 tbsp, so the standard rate is 0.8 tbsp per gallon of soil.
const STANDARD_TBSP_PER_GALLON = 4 / 5;

const SOIL_SIZES = [1, 2, 3, 5, 7, 10, 15, 20, 30, 45, 60];

// Feeding strength presets around the Dr. Earth 1/4-cup-per-5-gallon standard.
const STRENGTHS = [
  { label: "Light", tbspPerGallon: STANDARD_TBSP_PER_GALLON / 2 },
  { label: "Standard", tbspPerGallon: STANDARD_TBSP_PER_GALLON },
  { label: "Heavy", tbspPerGallon: STANDARD_TBSP_PER_GALLON * 2 },
];

// Flower Girl (3-9-4) is fed at a heavier rate for late bloom.
const BLOOM_MULTIPLIER = 1.5;

// Schedule: feed every 2 weeks starting 3 weeks after first true leaves.
// The pot is pre-amended, so the first feed is held back a couple of extra
// weeks. Weeks 3-7 use Dr. Earth 4-4-4 (veg), then switch to Flower Girl
// 3-9-4 for late bloom through week 15.
const FIRST_FEED_WEEK = 3;
const TRANSITION_WEEK = 8;
const TOTAL_WEEKS = 16;
const FEEDING_STEP_WEEKS = 2;

function addWeeks(date, weeks) {
  const result = new Date(date);
  result.setDate(result.getDate() + weeks * 7);
  return result;
}

function parseDateInput(value) {
  if (!value) return null;
  // Build a local date so the YYYY-MM-DD input is not shifted by timezone.
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(date) {
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

function formatAmount(tablespoons) {
  return `${round2(tablespoons)} tbsp (~${round2(tablespoons * 3)} tsp / ${round2(
    tablespoons / 16
  )} cup)`;
}

function buildSchedule(firstLeaves, gallons, tbspPerGallon) {
  const entries = [];
  for (
    let week = FIRST_FEED_WEEK;
    week <= TOTAL_WEEKS;
    week += FEEDING_STEP_WEEKS
  ) {
    const isBloom = week > TRANSITION_WEEK;
    const perGallon = tbspPerGallon * (isBloom ? BLOOM_MULTIPLIER : 1);
    entries.push({
      week,
      date: addWeeks(firstLeaves, week),
      product: isBloom ? "Flower Girl 3-9-4" : "Dr. Earth 4-4-4",
      stage: isBloom ? "Bloom" : "Veg",
      tbsp: perGallon * gallons,
    });
  }
  return entries.map((entry, index, all) => ({
    ...entry,
    // First bloom entry is the switch; the one before it is the last veg feed.
    isBloomStart: entry.stage === "Bloom" && all[index - 1]?.stage === "Veg",
    isTransition: entry.stage === "Veg" && all[index + 1]?.stage === "Bloom",
    amount: formatAmount(entry.tbsp),
  }));
}

const DrEarthOrganicFeeding = () => {
  const [soilSize, setSoilSize] = useState(5);
  const [customSoil, setCustomSoil] = useState("");
  const [strengthIndex, setStrengthIndex] = useState(1);
  const [firstLeavesInput, setFirstLeavesInput] = useState("");

  const strength = STRENGTHS[strengthIndex];
  const gallons = customSoil !== "" ? Number(customSoil) : soilSize;
  const validGallons = Number.isFinite(gallons) && gallons > 0;

  const firstLeaves = parseDateInput(firstLeavesInput);

  const schedule = useMemo(
    () =>
      firstLeaves && validGallons
        ? buildSchedule(firstLeaves, gallons, strength.tbspPerGallon)
        : [],
    [firstLeaves, validGallons, gallons, strength.tbspPerGallon]
  );

  const now = new Date();
  const nextFeed = schedule.find((entry) => entry.date >= now) || schedule[0];
  const bloomStartEntry = schedule.find((entry) => entry.isBloomStart);

  const rate444 = validGallons
    ? formatAmount(strength.tbspPerGallon * gallons)
    : "—";
  const rateFlower = validGallons
    ? formatAmount(strength.tbspPerGallon * BLOOM_MULTIPLIER * gallons)
    : "—";

  return (
    <div className="bg-black text-white flex flex-col items-center">
      <Helmet>
        <title>Dr. Earth Organic Feeding Schedule</title>
        <meta
          name="description"
          content="Dr. Earth organic feeding calculator and calendar for 4-4-4 and Flower Girl 3-9-4."
        />
      </Helmet>

      <div className="relative w-full min-h-screen bg-gradient-to-br from-gray-900 to-[#220033] text-white flex flex-col items-center overflow-y-auto">
        <header className="w-full bg-gradient-to-b from-purple-700 to-purple-900 text-white rounded-lg p-6 shadow-2xl mb-8 border-2 border-purple-800">
          <div className="w-32 h-1 bg-white mx-auto mt-4 rounded-full opacity-75" />
          <h1 className="text-center text-3xl font-bold mt-4">
            Dr. Earth Organic Feeding
          </h1>
          <p className="text-center text-purple-200 mt-2">
            Calculator &amp; schedule for 4-4-4 and Flower Girl 3-9-4
          </p>
        </header>

        <div className="w-full max-w-4xl px-4 pb-16 space-y-10">
          {/* Calculator */}
          <section className="border-2 border-white rounded-lg p-6 shadow-lg shadow-purple-800">
            <h2 className="text-2xl font-bold mb-6">🧮 Fertilizer Calculator</h2>

            <div className="grid gap-6 md:grid-cols-2">
              <div>
                <label className="block text-sm text-purple-200 mb-2">
                  Soil size
                </label>
                <select
                  value={soilSize}
                  onChange={(event) => {
                    setSoilSize(Number(event.target.value));
                    setCustomSoil("");
                  }}
                  className="w-full rounded-md bg-gray-900 border-2 border-purple-700 p-3 text-white"
                >
                  {SOIL_SIZES.map((size) => (
                    <option key={size} value={size}>
                      {size} gallon{size === 1 ? "" : "s"}
                    </option>
                  ))}
                </select>

                <label className="block text-sm text-purple-200 mt-4 mb-2">
                  Or enter a custom size (gallons)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={customSoil}
                  onChange={(event) => setCustomSoil(event.target.value)}
                  placeholder="e.g. 25"
                  className="w-full rounded-md bg-gray-900 border-2 border-purple-700 p-3 text-white"
                />
              </div>

              <div>
                <label className="block text-sm text-purple-200 mb-2">
                  Feeding strength
                </label>
                <div className="flex gap-2">
                  {STRENGTHS.map((option, index) => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setStrengthIndex(index)}
                      className={`flex-1 rounded-md border-2 p-3 transition-colors ${
                        index === strengthIndex
                          ? "border-yellow-400 bg-purple-700 text-white"
                          : "border-purple-700 bg-gray-900 text-gray-300 hover:bg-purple-900"
                      }`}
                    >
                      {option.label}
                      <span className="block text-xs">
                        {option.tbspPerGallon} tbsp/gal
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Dr. Earth cannabis standard: 1/4 cup per 5 gallons (~
                  {STANDARD_TBSP_PER_GALLON} tbsp per gallon).
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border-2 border-green-500 bg-black/40 p-4">
                <p className="text-sm text-green-300 uppercase tracking-wide">
                  Dr. Earth 4-4-4 (weeks 3–7)
                </p>
                <p className="text-xl font-bold mt-1">{rate444}</p>
                <p className="text-xs text-gray-400 mt-1">Per feeding.</p>
              </div>
              <div className="rounded-lg border-2 border-pink-500 bg-black/40 p-4">
                <p className="text-sm text-pink-300 uppercase tracking-wide">
                  Flower Girl 3-9-4 (bloom)
                </p>
                <p className="text-xl font-bold mt-1">{rateFlower}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Late bloom rate ({BLOOM_MULTIPLIER}&times; veg). Per feeding.
                </p>
              </div>
            </div>

            {validGallons && (
              <p className="text-sm text-gray-300 mt-4">
                Making <strong>{gallons} gallons</strong> of soil at the{" "}
                <strong>{strength.label.toLowerCase()}</strong> rate.
              </p>
            )}
          </section>

          {/* Calendar */}
          <section className="border-2 border-white rounded-lg p-6 shadow-lg shadow-purple-800">
            <h2 className="text-2xl font-bold mb-2">📅 Feeding Calendar</h2>
            <p className="text-sm text-purple-200 mb-6">
              Enter the date your plant got its first true leaves. The first feed
              is 3 weeks after (the pot is pre-amended), then every 2 weeks
              after that.
            </p>

            <label className="block text-sm text-purple-200 mb-2">
              Date of first true leaves
            </label>
            <input
              type="date"
              value={firstLeavesInput}
              onChange={(event) => setFirstLeavesInput(event.target.value)}
              className="w-full max-w-xs rounded-md bg-gray-900 border-2 border-purple-700 p-3 text-white"
            />

            {firstLeaves && !validGallons && (
              <p className="text-sm text-yellow-300 mt-4">
                Enter a soil size above to see the amount for each feeding.
              </p>
            )}

            {firstLeaves && validGallons && (
              <>
                <div className="grid gap-4 sm:grid-cols-3 mt-6">
                  <div className="rounded-lg bg-black/40 border border-purple-700 p-4">
                    <p className="text-xs text-gray-400 uppercase">First feed</p>
                    <p className="font-bold">
                      {formatDate(addWeeks(firstLeaves, FIRST_FEED_WEEK))}
                    </p>
                  </div>
                  <div className="rounded-lg bg-black/40 border border-purple-700 p-4">
                    <p className="text-xs text-gray-400 uppercase">
                      Switch to Flower Girl
                    </p>
                    <p className="font-bold">
                      {bloomStartEntry
                        ? formatDate(bloomStartEntry.date)
                        : "—"}
                    </p>
                  </div>
                  <div className="rounded-lg bg-black/40 border border-purple-700 p-4">
                    <p className="text-xs text-gray-400 uppercase">
                      Feedings listed
                    </p>
                    <p className="font-bold">{schedule.length}</p>
                  </div>
                </div>

                <ol className="mt-6 space-y-3">
                  {schedule.map((entry) => {
                    const isPast = entry.date < now;
                    const isNext =
                      nextFeed && entry.week === nextFeed.week && !isPast;
                    return (
                      <li
                        key={entry.week}
                        className={`rounded-lg border-2 p-4 flex flex-wrap items-center justify-between gap-2 ${
                          isNext
                            ? "border-yellow-400 bg-purple-900/60"
                            : "border-purple-700 bg-black/40"
                        } ${isPast ? "opacity-60" : ""}`}
                      >
                        <div>
                          <p className="font-bold text-lg">
                            Week {entry.week} &middot; {formatDate(entry.date)}
                            {isNext && (
                              <span className="ml-2 text-xs align-middle bg-yellow-400 text-black rounded px-2 py-0.5">
                                NEXT
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-gray-300">
                            Add{" "}
                            <span
                              className={
                                entry.stage === "Bloom"
                                  ? "text-pink-300 font-semibold"
                                  : "text-green-300 font-semibold"
                              }
                            >
                              {entry.product}
                            </span>{" "}
                            &middot; {entry.amount}
                          </p>
                          {entry.isTransition && (
                            <p className="text-xs text-yellow-300 mt-1">
                              Last 4-4-4 veg feed — prepare to switch.
                            </p>
                          )}
                          {entry.isBloomStart && (
                            <p className="text-xs text-pink-300 mt-1">
                              Bloom stage begins — first Flower Girl 3-9-4 feed.
                            </p>
                          )}
                        </div>
                        <span
                          className={`text-xs uppercase tracking-wide rounded-full px-3 py-1 ${
                            entry.stage === "Bloom"
                              ? "bg-pink-600"
                              : "bg-green-700"
                          }`}
                        >
                          {entry.stage}
                        </span>
                      </li>
                    );
                  })}
                </ol>

                <p className="text-xs text-gray-400 mt-4">
                  Feedings are every 2 weeks. Weeks 3–7 use Dr. Earth 4-4-4;
                  from week 9 on, switch to Flower Girl 3-9-4 through late
                  bloom.
                </p>
              </>
            )}
          </section>
        </div>
      </div>

      <div className="w-full p-4 mx-auto mt-20 border-8 border-white rounded-lg shadow-lg shadow-purple-800 h-[200px]">
        <p className="text-white text-4xl font-bold text-center">
          Ads here soon
        </p>
      </div>
    </div>
  );
};

export default DrEarthOrganicFeeding;
